// /////////////////////////////////////////////////////////////
// [ファイル] CustomerPicker.tsx
// [概要] CRM 顧客を名前で検索して選ぶ Autocomplete（推進案件機能統合 リリース1）
//        売主（1 人）・区画の顧客（複数）・仲介業者（法人）の選択に共用する。
//        検索は search_crm_customers（個人・法人横断）。新規登録は CRM 画面側で行う。
// /////////////////////////////////////////////////////////////
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { ep } from '../../api/endpoints';

export interface CustomerOption {
  id: string;
  display_name: string;
  customer_type: 'individual' | 'corporate';
}

interface Props {
  value: CustomerOption | null;
  onChange: (v: CustomerOption | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  corporateOnly?: boolean;
  disabled?: boolean;
  error?: string;
  size?: 'small' | 'medium';
}

const SEARCH_URL = ep('search_crm_customers');

export default function CustomerPicker({ value, onChange, label, placeholder = '顧客名で検索', required, corporateOnly, disabled, error, size = 'small' }: Props) {
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  const search = (q: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q) { setOptions([]); setLoading(false); return; }
    setLoading(true);
    const mySeq = ++seq.current;
    timer.current = setTimeout(async () => {
      try {
        const res = await axios.post(SEARCH_URL, { query: q });
        if (mySeq !== seq.current) return;
        const rows: CustomerOption[] = res.data.status === 'success' ? (res.data.results || []) : [];
        setOptions(corporateOnly ? rows.filter((r) => r.customer_type === 'corporate') : rows);
      } catch {
        if (mySeq === seq.current) setOptions([]);
      } finally {
        if (mySeq === seq.current) setLoading(false);
      }
    }, 300);
  };

  return (
    <Autocomplete
      size={size}
      disabled={disabled}
      options={options}
      value={value}
      loading={loading}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.display_name || ''}
      filterOptions={(o) => o}
      noOptionsText={input ? '該当する顧客がありません（CRM で登録してください）' : '顧客名を入力して検索'}
      onInputChange={(_, v, reason) => { setInput(v); if (reason === 'input') search(v); }}
      onChange={(_, v) => onChange(v)}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={o.customer_type === 'individual' ? '個人' : '法人'} size="small" variant="outlined" color={o.customer_type === 'individual' ? 'success' : 'info'} />
            <span>{o.display_name}</span>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} required={required} error={!!error} helperText={error} />
      )}
    />
  );
}
